/**
 * v3.15-1229 SMART-INTAKE-002 — Client Payment Methods Intake block.
 *
 * Renders the Workflow Mode selector (Standard / Credit Card / ACH) plus
 * the dynamic multi-add forms for credit cards and ACH accounts.  The
 * component is a controlled block — the parent owns the state via
 * `mode`, `creditCards`, `achAccounts` + their setters.
 *
 * The parent posts the client first, then iterates
 * ``creditCards`` and ``achAccounts`` and POSTs each to the payment-
 * method endpoints. No calls are issued from this component.
 */
import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Trash2, CreditCard, Landmark } from 'lucide-react';

export const emptyCreditCard = () => ({
  payment_nickname: '',
  bank_name: '',
  cardholder_name: '',
  card_number: '',
  card_brand: '',
  expiration_month: '',
  expiration_year: '',
  cvv: '',
  billing_zip: '',
  credit_limit: '',
  current_balance: '',
  minimum_payment: '',
  interest_rate: '',
  is_default: false,
  notes: '',
});

export const emptyAch = () => ({
  payment_nickname: '',
  account_holder_name: '',
  bank_name: '',
  routing_number: '',
  account_number: '',
  account_type: '',
  authorization_given: false,
  is_default: false,
  notes: '',
});

const MODE_BTNS = [
  { value: 'standard',    label: 'Standard Client',  testid: 'intake-mode-standard' },
  { value: 'credit_card', label: 'Credit Card',      testid: 'intake-mode-credit-card' },
  { value: 'ach',         label: 'ACH',              testid: 'intake-mode-ach' },
];

const ClientPaymentMethodsIntake = ({
  mode,
  onModeChange,
  creditCards,
  onCreditCardsChange,
  achAccounts,
  onAchAccountsChange,
}) => {
  const updateCC = (idx, field, value) => {
    const next = creditCards.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
    // Ensure only one default at a time
    if (field === 'is_default' && value === true) {
      onCreditCardsChange(next.map((c, i) => (i === idx ? c : { ...c, is_default: false })));
    } else {
      onCreditCardsChange(next);
    }
  };
  const updateACH = (idx, field, value) => {
    const next = achAccounts.map((a, i) => (i === idx ? { ...a, [field]: value } : a));
    if (field === 'is_default' && value === true) {
      onAchAccountsChange(next.map((a, i) => (i === idx ? a : { ...a, is_default: false })));
    } else {
      onAchAccountsChange(next);
    }
  };

  const addCC = () => onCreditCardsChange([...creditCards, emptyCreditCard()]);
  const removeCC = (idx) => onCreditCardsChange(creditCards.filter((_, i) => i !== idx));
  const addACH = () => onAchAccountsChange([...achAccounts, emptyAch()]);
  const removeACH = (idx) => onAchAccountsChange(achAccounts.filter((_, i) => i !== idx));

  return (
    <div data-testid="client-payment-methods-intake" className="space-y-4">
      {/* ── Workflow Mode Selector ─────────────────────────────────── */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Intake Workflow</h3>
        <div className="flex flex-wrap gap-2" data-testid="intake-mode-selector">
          {MODE_BTNS.map((b) => {
            const active = mode === b.value;
            return (
              <button
                key={b.value}
                type="button"
                data-testid={b.testid}
                onClick={() => onModeChange(b.value)}
                className={`px-4 py-2 rounded-md text-xs font-semibold border transition
                  ${active
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
              >
                {b.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          The client is created the same way in all three modes. Selecting
          Credit Card or ACH additionally attaches payment methods to the
          new client — multiple payment methods per client are supported.
        </p>
      </div>

      {/* ── Credit Card Mode ───────────────────────────────────────── */}
      {mode === 'credit_card' && (
        <div data-testid="cc-section" className="border rounded-md p-4 bg-slate-50 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Credit Cards ({creditCards.length})
            </h4>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addCC}
              data-testid="cc-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Credit Card
            </Button>
          </div>

          {creditCards.length === 0 && (
            <p className="text-xs text-slate-500 italic">
              No credit cards added yet. Click &quot;Add Credit Card&quot; to attach one or more cards.
            </p>
          )}

          {creditCards.map((c, i) => (
            <div
              key={`cc-${i}`}
              data-testid={`cc-row-${i}`}
              className="bg-white border rounded-md p-3 space-y-2 relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                  Card #{i + 1}
                </span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={!!c.is_default}
                      onCheckedChange={(v) => updateCC(i, 'is_default', !!v)}
                      data-testid={`cc-default-${i}`}
                    />
                    Default
                  </label>
                  <button
                    type="button"
                    onClick={() => removeCC(i)}
                    className="text-rose-500 hover:text-rose-700"
                    title="Remove"
                    data-testid={`cc-remove-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Payment Nickname</Label>
                  <Input
                    value={c.payment_nickname}
                    onChange={(e) => updateCC(i, 'payment_nickname', e.target.value)}
                    placeholder="Personal Amex"
                    data-testid={`cc-nickname-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Bank / Issuer *</Label>
                  <Input
                    value={c.bank_name}
                    onChange={(e) => updateCC(i, 'bank_name', e.target.value)}
                    placeholder="Chase"
                    data-testid={`cc-bank-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Cardholder Name *</Label>
                  <Input
                    value={c.cardholder_name}
                    onChange={(e) => updateCC(i, 'cardholder_name', e.target.value)}
                    placeholder="John A. Doe"
                    data-testid={`cc-cardholder-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Card Brand</Label>
                  <Select
                    value={c.card_brand || ''}
                    onValueChange={(v) => updateCC(i, 'card_brand', v)}
                  >
                    <SelectTrigger data-testid={`cc-brand-${i}`}>
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visa">Visa</SelectItem>
                      <SelectItem value="mastercard">MasterCard</SelectItem>
                      <SelectItem value="amex">American Express</SelectItem>
                      <SelectItem value="discover">Discover</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Card Number *</Label>
                  <Input
                    value={c.card_number}
                    onChange={(e) => updateCC(i, 'card_number', e.target.value)}
                    placeholder="4111 1111 1111 1111"
                    data-testid={`cc-number-${i}`}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 col-span-2">
                  <div>
                    <Label className="text-xs">Exp. Month</Label>
                    <Input
                      value={c.expiration_month}
                      onChange={(e) => updateCC(i, 'expiration_month', e.target.value)}
                      placeholder="MM"
                      data-testid={`cc-exp-month-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Exp. Year</Label>
                    <Input
                      value={c.expiration_year}
                      onChange={(e) => updateCC(i, 'expiration_year', e.target.value)}
                      placeholder="YYYY"
                      data-testid={`cc-exp-year-${i}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CVV</Label>
                    <Input
                      value={c.cvv}
                      onChange={(e) => updateCC(i, 'cvv', e.target.value)}
                      placeholder="123"
                      data-testid={`cc-cvv-${i}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Billing Zip</Label>
                  <Input
                    value={c.billing_zip}
                    onChange={(e) => updateCC(i, 'billing_zip', e.target.value)}
                    placeholder="90210"
                    data-testid={`cc-zip-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Credit Limit</Label>
                  <Input
                    type="number"
                    value={c.credit_limit}
                    onChange={(e) => updateCC(i, 'credit_limit', e.target.value)}
                    placeholder="0.00"
                    data-testid={`cc-limit-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Current Balance</Label>
                  <Input
                    type="number"
                    value={c.current_balance}
                    onChange={(e) => updateCC(i, 'current_balance', e.target.value)}
                    placeholder="0.00"
                    data-testid={`cc-balance-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Minimum Payment</Label>
                  <Input
                    type="number"
                    value={c.minimum_payment}
                    onChange={(e) => updateCC(i, 'minimum_payment', e.target.value)}
                    placeholder="0.00"
                    data-testid={`cc-min-payment-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Interest Rate (APR %)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={c.interest_rate}
                    onChange={(e) => updateCC(i, 'interest_rate', e.target.value)}
                    placeholder="0.00"
                    data-testid={`cc-apr-${i}`}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={c.notes}
                    onChange={(e) => updateCC(i, 'notes', e.target.value)}
                    placeholder="Optional notes"
                    data-testid={`cc-notes-${i}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── ACH Mode ───────────────────────────────────────────────── */}
      {mode === 'ach' && (
        <div data-testid="ach-section" className="border rounded-md p-4 bg-slate-50 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Landmark className="h-4 w-4" /> ACH Accounts ({achAccounts.length})
            </h4>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={addACH}
              data-testid="ach-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add ACH Account
            </Button>
          </div>

          {achAccounts.length === 0 && (
            <p className="text-xs text-slate-500 italic">
              No ACH accounts added yet. Click &quot;Add ACH Account&quot; to attach one or more accounts.
            </p>
          )}

          {achAccounts.map((a, i) => (
            <div
              key={`ach-${i}`}
              data-testid={`ach-row-${i}`}
              className="bg-white border rounded-md p-3 space-y-2 relative"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                  Account #{i + 1}
                </span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={!!a.is_default}
                      onCheckedChange={(v) => updateACH(i, 'is_default', !!v)}
                      data-testid={`ach-default-${i}`}
                    />
                    Default
                  </label>
                  <button
                    type="button"
                    onClick={() => removeACH(i)}
                    className="text-rose-500 hover:text-rose-700"
                    title="Remove"
                    data-testid={`ach-remove-${i}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Payment Nickname</Label>
                  <Input
                    value={a.payment_nickname}
                    onChange={(e) => updateACH(i, 'payment_nickname', e.target.value)}
                    placeholder="Primary Checking"
                    data-testid={`ach-nickname-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Account Holder Name *</Label>
                  <Input
                    value={a.account_holder_name}
                    onChange={(e) => updateACH(i, 'account_holder_name', e.target.value)}
                    placeholder="John A. Doe"
                    data-testid={`ach-holder-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Bank Name *</Label>
                  <Input
                    value={a.bank_name}
                    onChange={(e) => updateACH(i, 'bank_name', e.target.value)}
                    placeholder="Bank of America"
                    data-testid={`ach-bank-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Account Type</Label>
                  <Select
                    value={a.account_type || ''}
                    onValueChange={(v) => updateACH(i, 'account_type', v)}
                  >
                    <SelectTrigger data-testid={`ach-type-${i}`}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Routing Number *</Label>
                  <Input
                    value={a.routing_number}
                    onChange={(e) => updateACH(i, 'routing_number', e.target.value)}
                    placeholder="123456789"
                    data-testid={`ach-routing-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Account Number *</Label>
                  <Input
                    value={a.account_number}
                    onChange={(e) => updateACH(i, 'account_number', e.target.value)}
                    placeholder="000123456789"
                    data-testid={`ach-account-${i}`}
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <Checkbox
                    id={`ach-auth-${i}`}
                    checked={!!a.authorization_given}
                    onCheckedChange={(v) => updateACH(i, 'authorization_given', !!v)}
                    data-testid={`ach-auth-${i}`}
                  />
                  <Label htmlFor={`ach-auth-${i}`} className="cursor-pointer text-xs">
                    Client has authorized ACH debit from this account
                  </Label>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={a.notes}
                    onChange={(e) => updateACH(i, 'notes', e.target.value)}
                    placeholder="Optional notes"
                    data-testid={`ach-notes-${i}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientPaymentMethodsIntake;
